import { useState } from 'react';

import { ApiError, api } from '@/api/client';
import type { ApprovalItem, ApprovalPolicy } from '@/api/types';
import { useApi } from '@/api/useApi';
import {
  Avatar,
  Button,
  Caption,
  Card,
  CardHead,
  ErrorState,
  Eyebrow,
  Field,
  Loading,
  Notice,
  Page,
  PageHead,
  PolicySwitch,
  Tag,
  TextArea,
} from '@/components/ui';
import { formatDate, formatRange, humanise } from '@/lib/format';

/**
 * Screen 03 — approving what the family sees.
 *
 * This screen exists because the source specification defined the derived
 * family update but named no owner and no interface for it. One reviewer, one
 * queue, four decisions, and the agency's policy stated on the same screen.
 *
 * The three blocks are the whole point: what is already published, what is
 * waiting on this person, and what the family will never see.
 */
export function ApprovalsPage() {
  const queue = useApi<{ queue: ApprovalItem[] }>('/agency/approvals');
  const policy = useApi<ApprovalPolicy>('/agency/policy/approval');
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [editReason, setEditReason] = useState('');
  const [returnMessage, setReturnMessage] = useState('');
  const [returning, setReturning] = useState(false);

  if (queue.loading) return <Loading />;
  if (queue.error)
    return (
      <Page>
        <ErrorState message={queue.error.message} onRetry={queue.reload} />
      </Page>
    );

  const items = queue.data?.queue ?? [];
  const item = items[Math.min(index, Math.max(items.length - 1, 0))];

  async function decide(decision: string, extra: Record<string, unknown> = {}) {
    if (!item) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.post(`/agency/approvals/${item.id}/decide`, { decision, ...extra });
      setEditing(false);
      setReturning(false);
      setEditedSummary('');
      setEditReason('');
      setReturnMessage('');
      // Stay on the same position: the next item slides into it, which is what
      // makes a queue feel like a queue.
      setIndex((current) => Math.max(0, Math.min(current, items.length - 2)));
      await queue.reload();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page>
      <PageHead
        title="Approve what the family sees"
        subtitle={
          items.length === 0
            ? 'Nothing waiting'
            : `${index + 1} of ${items.length} in the queue`
        }
      />

      {problem ? (
        <Notice tone="urgent" title="Could not complete that">
          {problem}
        </Notice>
      ) : null}

      {items.length === 0 ? (
        <Notice tone="positive" title="The queue is clear">
          Structured task results publish on submit, so families are never waiting on this queue
          to learn whether care happened — only for the caregiver&apos;s written summary.
        </Notice>
      ) : item ? (
        <div className="split-half">
          {/* ------------------------------------------ the caregiver's note */}
          <Card variant="flush">
            <CardHead
              title={
                <div className="row">
                  <Avatar person={item.caregiver} size="lg" />
                  <div>
                    <div className="cell-title">{item.caregiver.name}</div>
                    <div className="cell-sub">
                      {item.recipient_name} · {formatDate(item.scheduled_start)}{' '}
                      {formatRange(item.scheduled_start, item.scheduled_end)}
                    </div>
                  </div>
                </div>
              }
              actions={<Tag label={humanise(item.input_method)} />}
            />

            <div className="card__body stack">
              {/* Already with the family. */}
              <div
                style={{
                  background: 'var(--green-tint)',
                  border: '1px solid var(--green-tint-border)',
                  padding: 'var(--s-12)',
                }}
              >
                <strong style={{ color: 'var(--green)' }}>
                  Auto-published to family · structured
                </strong>
                <ul style={{ margin: 'var(--s-8) 0 0', paddingLeft: '1.1rem' }}>
                  {item.published.categories.map((category, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {category.name} — {category.outcome.toLowerCase()}
                      {category.detail ? (
                        <span className="caption"> ({category.detail})</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Waiting on this reviewer. */}
              <div
                style={{
                  background: 'var(--gold-tint)',
                  borderLeft: '3px solid var(--gold)',
                  padding: 'var(--s-12)',
                }}
              >
                <strong>Waiting for approval · free text</strong>
                <p style={{ marginTop: 'var(--s-8)' }}>
                  &ldquo;{item.awaiting.summary}&rdquo;
                </p>
                {item.awaiting.due_at ? (
                  <Caption>Auto-publishes {formatDate(item.awaiting.due_at)} if unreviewed.</Caption>
                ) : null}
              </div>

              {/* Never reaches the family. */}
              {item.internal ? (
                <div
                  style={{
                    background: 'var(--page)',
                    border: '1px dashed var(--border)',
                    padding: 'var(--s-12)',
                  }}
                >
                  <Eyebrow>Internal observation · not shown to family</Eyebrow>
                  <p style={{ marginTop: 'var(--s-6)' }}>&ldquo;{item.internal}&rdquo;</p>
                </div>
              ) : null}
            </div>
          </Card>

          {/* ------------------------------------------ the decision */}
          <Card variant="flush">
            <CardHead title="Reviewer decision" />
            <div className="card__body stack">
              <div style={{ border: '1px solid var(--border)', padding: 'var(--s-12)' }}>
                <Eyebrow>What the family will read</Eyebrow>
                <p style={{ marginTop: 'var(--s-6)' }}>
                  &ldquo;{editing && editedSummary ? editedSummary : item.awaiting.summary}&rdquo;
                </p>
                {editing && editedSummary ? (
                  <Caption>
                    Edited. The original text, editor, time and reason are preserved in audit
                    history.
                  </Caption>
                ) : null}
              </div>

              {editing ? (
                <div className="stack stack--tight">
                  <Field label="Edited summary">
                    {(props) => (
                      <TextArea
                        {...props}
                        value={editedSummary}
                        onChange={(event) => setEditedSummary(event.target.value)}
                        autoFocus
                      />
                    )}
                  </Field>
                  <Field
                    label="Reason for the edit"
                    hint="Kept with the original text. Required."
                    error={editReason.trim() ? null : 'A reason is required'}
                  >
                    {(props) => (
                      <TextArea
                        {...props}
                        value={editReason}
                        onChange={(event) => setEditReason(event.target.value)}
                      />
                    )}
                  </Field>
                  <div className="row">
                    <Button
                      onClick={() =>
                        decide('publish_with_edit', {
                          edited_summary: editedSummary,
                          edit_reason: editReason,
                        })
                      }
                      busy={busy}
                      disabled={!editedSummary.trim() || !editReason.trim()}
                    >
                      Publish edited
                    </Button>
                    <Button variant="ghost" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : returning ? (
                <div className="stack stack--tight">
                  <Field
                    label="What should the caregiver change?"
                    error={returnMessage.trim() ? null : 'Tell them what to change'}
                  >
                    {(props) => (
                      <TextArea
                        {...props}
                        value={returnMessage}
                        onChange={(event) => setReturnMessage(event.target.value)}
                        autoFocus
                      />
                    )}
                  </Field>
                  <div className="row">
                    <Button
                      onClick={() => decide('return', { message: returnMessage })}
                      busy={busy}
                      disabled={!returnMessage.trim()}
                    >
                      Return to caregiver
                    </Button>
                    <Button variant="ghost" onClick={() => setReturning(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="row row--wrap">
                  <Button onClick={() => decide('publish')} busy={busy}>
                    Approve and publish
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setEditedSummary(item.awaiting.summary ?? '');
                      setEditing(true);
                    }}
                  >
                    Approve with edit
                  </Button>
                  <Button variant="secondary" onClick={() => setReturning(true)}>
                    Return to caregiver
                  </Button>
                  <Button variant="urgent" onClick={() => decide('raise_plan_review')} busy={busy}>
                    Raise plan review
                  </Button>
                </div>
              )}

              {items.length > 1 ? (
                <div className="row row--between">
                  <Button
                    variant="ghost"
                    onClick={() => setIndex((i) => Math.max(0, i - 1))}
                    disabled={index === 0}
                  >
                    ‹ Previous
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setIndex((i) => Math.min(items.length - 1, i + 1))}
                    disabled={index >= items.length - 1}
                  >
                    Next ›
                  </Button>
                </div>
              ) : null}

              <div>
                <Eyebrow>Agency setting</Eyebrow>
                <div style={{ marginTop: 'var(--s-6)' }}>
                  {(policy.data?.switches ?? []).map((sw) => (
                    <PolicySwitch
                      key={sw.key}
                      on={sw.on}
                      label={sw.label}
                      why={sw.why}
                      locked={sw.locked}
                    />
                  ))}
                </div>
              </div>

              <Caption>
                Every edit preserves the original text, the editor, the time and the reason.
              </Caption>
            </div>
          </Card>
        </div>
      ) : null}
    </Page>
  );
}
